import React, { useState } from 'react';
import { Upload, Search, Database, FileText, Loader2, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';

const KnowledgeBase = () => {
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'proposal'); // Default type

    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/knowledge/upload`, formData);
      toast.success(`Indexed ${res.data.chunks_added} chunks from ${res.data.filename}`);
    } catch (err) {
      toast.error("Upload failed");
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/knowledge/search`, {
        params: { q: searchQuery }
      });
      setResults(res.data);
    } catch (err) {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Knowledge Base</h1>
          <p className="text-muted-foreground">Central repository for past proposals and technical data.</p>
        </div>
        <div className="flex flex-col gap-6">
          {/* Info Alert */}
          <Alert className="bg-blue-50/50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800 text-blue-700 dark:text-blue-300">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertTitle className="text-blue-800 dark:text-blue-200">Train the AI on your "Work Follow"</AlertTitle>
            <AlertDescription className="text-blue-600 dark:text-blue-300">
              Upload your old successful tenders (PDF/Text) here. The AI analyzes them to learn your company's writing style, 
              methodology, and workflow. These learnings are automatically applied when you draft new proposals.
            </AlertDescription>
          </Alert>

          <div className="flex justify-end">
            <div className="relative">
              <input 
                type="file" 
                id="kb-upload" 
                className="hidden" 
                onChange={handleFileUpload}
                disabled={uploading}
                accept=".pdf,.txt"
              />
              <label htmlFor="kb-upload" className="cursor-pointer">
                <div className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 gap-2 shadow-md shadow-primary/20">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? 'Indexing...' : 'Upload Past Proposal'}
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <Card className="bg-card border-border shadow-sm">
        <CardContent className="p-6">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input 
                placeholder="Search for 'pricing models', 'security compliance', 'methodology'..." 
                className="pl-10 bg-background border-input text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary/20"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button onClick={handleSearch} disabled={searching} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              {searching ? 'Searching...' : 'Search'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Area */}
      <div className="grid gap-6">
        {results.length > 0 ? (
          results.map((result, index) => (
            <Card key={index} className="bg-card border-border hover:border-primary/50 transition-colors group shadow-sm hover:shadow-md">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                   <div className="flex items-center gap-2">
                     <FileText className="h-4 w-4 text-primary" />
                     <span className="font-medium text-sm text-foreground">{result.metadata.source}</span>
                   </div>
                   <Badge variant="outline" className="bg-muted text-muted-foreground text-[10px] border-border">
                     Score: {Math.round((1 - result.distance) * 100)}%
                   </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  "{result.content}"
                </p>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="text-center py-20 border-2 border-dashed border-border rounded-lg bg-muted/20">
            <Database className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground">No results found</h3>
            <p className="text-sm text-muted-foreground">Try searching for specific terms or upload more documents.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeBase;
